import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoverCourtIntelligenceWizard,
  getCourtIntelligenceWizardLogs,
  getCourtIntelligenceWizardOptions,
  getCourtIntelligenceWizardProgress,
  triggerCourtIntelligenceWizard,
  type CourtWizardDiscoverResult,
  type CourtWizardEndpointOption,
  type CourtWizardLogsResult,
  type CourtWizardOptionsResult,
  type CourtWizardProgressResult,
  type ManualCourtExtractionTool,
} from './templateManagementService';

interface WizardRunContext {
  jurisdiction_code: string;
  practice_area: string;
}

interface CourtIntelligenceWizardPanelProps {
  defaultPracticeArea?: string;
  defaultJurisdiction?: string;
  onRunCompleted?: (context: WizardRunContext) => void | Promise<void>;
}

interface EndpointOverrides {
  forms_url: string;
  judges_url: string;
  fees_url: string;
  rules_url: string;
}

type EndpointKey = keyof EndpointOverrides;

const FINAL_STATES = new Set(['completed', 'partial_failed', 'failed']);
const EMPTY_ENDPOINT_OPTIONS: Record<EndpointKey, CourtWizardEndpointOption[]> = {
  forms_url: [],
  judges_url: [],
  fees_url: [],
  rules_url: [],
};

function normalizePracticeArea(value: string): string {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
}

function extractJurisdictionCode(value?: string): string {
  const source = String(value || '').trim().toUpperCase();
  if (!source) return '';
  const exact = /^[A-Z]{2}-\d{5,6}$/.test(source) ? source : null;
  if (exact) return exact;
  const match = source.match(/[A-Z]{2}-\d{5,6}/);
  return match ? match[0] : source;
}

function statusClass(status: string): string {
  if (status === 'completed') return 'text-emerald-600 dark:text-emerald-400';
  if (status === 'running') return 'text-lex-accent dark:text-lex-accent';
  if (status === 'failed' || status === 'partial_failed') return 'text-rose-600 dark:text-rose-400';
  return 'text-gray-600 dark:text-gray-300';
}

function formatDate(value?: string | null): string {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
}

function endpointLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const pathText = parsed.pathname === '/' ? parsed.host : `${parsed.host}${parsed.pathname}`;
    return pathText;
  } catch {
    return url;
  }
}

function chooseEndpoint(
  current: string,
  options: CourtWizardEndpointOption[],
  recommended: string | null,
  optional = false,
): string {
  if (current && options.some((item) => item.url === current)) return current;
  if (recommended && options.some((item) => item.url === recommended)) return recommended;
  if (options.length > 0) return options[0].url;
  return optional ? '' : current;
}

function mergeEndpointOptions(
  base: CourtWizardEndpointOption[],
  selectedUrl: string,
  discoveredUrl?: string | null,
): CourtWizardEndpointOption[] {
  const merged = [...base];

  const ensureOption = (url: string, labelSuffix: string) => {
    if (!url || merged.some((item) => item.url === url)) return;
    merged.unshift({
      url,
      label: `${endpointLabel(url)} (${labelSuffix})`,
      source: 'heuristic',
      is_recommended: false,
      lifecycle_status: null,
      format_family: null,
      http_status: null,
    });
  };

  ensureOption(selectedUrl, 'Selected');
  ensureOption(discoveredUrl || '', 'Discovery Suggestion');

  return merged;
}

const CourtIntelligenceWizardPanel: React.FC<CourtIntelligenceWizardPanelProps> = ({
  defaultPracticeArea,
  defaultJurisdiction,
  onRunCompleted,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const [wizardOptions, setWizardOptions] = useState<CourtWizardOptionsResult | null>(null);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);

  const [jurisdictionCode, setJurisdictionCode] = useState<string>(extractJurisdictionCode(defaultJurisdiction));
  const [courtRegistryId, setCourtRegistryId] = useState<string>('');
  const [practiceArea, setPracticeArea] = useState<string>(normalizePracticeArea(defaultPracticeArea || 'family_law'));
  const [tools, setTools] = useState<Record<ManualCourtExtractionTool, boolean>>({
    forms: true,
    judges: true,
    fees: true,
  });

  const [endpoints, setEndpoints] = useState<EndpointOverrides>({
    forms_url: '',
    judges_url: '',
    fees_url: '',
    rules_url: '',
  });

  const [discoverResult, setDiscoverResult] = useState<CourtWizardDiscoverResult | null>(null);
  const [discovering, setDiscovering] = useState<boolean>(false);
  const [triggering, setTriggering] = useState<boolean>(false);
  const [progress, setProgress] = useState<CourtWizardProgressResult | null>(null);
  const [logs, setLogs] = useState<CourtWizardLogsResult | null>(null);
  const [operationId, setOperationId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const selectedTools = useMemo(
    () => (Object.keys(tools) as ManualCourtExtractionTool[]).filter((tool) => tools[tool]),
    [tools],
  );

  const isRunning = useMemo(() => {
    if (!progress) return false;
    return !FINAL_STATES.has(progress.status);
  }, [progress]);

  const selectedCourt = useMemo(() => {
    if (!wizardOptions) return null;
    return wizardOptions.courts.find((court) => court.id === courtRegistryId) || null;
  }, [courtRegistryId, wizardOptions]);

  const selectedCourtName = selectedCourt?.court_name || wizardOptions?.selected.court_name || '';
  const selectedRootUrl = selectedCourt?.base_url || wizardOptions?.selected.root_url || '';

  const endpointOptions = useMemo(() => {
    const base = wizardOptions?.endpoint_options || EMPTY_ENDPOINT_OPTIONS;
    return {
      forms_url: mergeEndpointOptions(base.forms_url, endpoints.forms_url, discoverResult?.recommended.forms_url),
      judges_url: mergeEndpointOptions(base.judges_url, endpoints.judges_url, discoverResult?.recommended.judges_url),
      fees_url: mergeEndpointOptions(base.fees_url, endpoints.fees_url, discoverResult?.recommended.fees_url),
      rules_url: mergeEndpointOptions(base.rules_url, endpoints.rules_url, discoverResult?.recommended.rules_url),
    };
  }, [discoverResult, endpoints, wizardOptions]);

  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
      try {
        setOptionsLoading(true);
        const result = await getCourtIntelligenceWizardOptions({
          jurisdiction_code: jurisdictionCode || undefined,
          court_registry_id: courtRegistryId || undefined,
        });
        if (!active) return;

        setWizardOptions(result);

        if (result.selected.jurisdiction_code && result.selected.jurisdiction_code !== jurisdictionCode) {
          setJurisdictionCode(result.selected.jurisdiction_code);
        }
        if (result.selected.court_registry_id && result.selected.court_registry_id !== courtRegistryId) {
          setCourtRegistryId(result.selected.court_registry_id);
        }

        setPracticeArea((prev) => {
          const allowed = new Set(result.practice_area_options.map((item) => item.value));
          if (prev && allowed.has(prev)) return prev;
          const preferredDefault = normalizePracticeArea(defaultPracticeArea || '');
          if (preferredDefault && allowed.has(preferredDefault)) return preferredDefault;
          return result.practice_area_options[0]?.value || prev || 'family_law';
        });

        setEndpoints((prev) => ({
          forms_url: chooseEndpoint(prev.forms_url, result.endpoint_options.forms_url, result.recommended.forms_url),
          judges_url: chooseEndpoint(prev.judges_url, result.endpoint_options.judges_url, result.recommended.judges_url),
          fees_url: chooseEndpoint(prev.fees_url, result.endpoint_options.fees_url, result.recommended.fees_url),
          rules_url: chooseEndpoint(prev.rules_url, result.endpoint_options.rules_url, result.recommended.rules_url, true),
        }));
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Failed to load wizard options');
      } finally {
        if (active) setOptionsLoading(false);
      }
    };

    void loadOptions();

    return () => {
      active = false;
    };
  }, [courtRegistryId, defaultPracticeArea, jurisdictionCode]);

  const validateForDiscover = (): string | null => {
    if (!jurisdictionCode.trim()) return 'Select a jurisdiction to continue.';
    if (!courtRegistryId.trim()) return 'Select a court to continue.';
    if (!selectedCourtName.trim()) return 'Selected court name is unavailable.';
    if (!selectedRootUrl.trim()) return 'Selected court has no official website configured yet.';
    return null;
  };

  const validateForTrigger = (): string | null => {
    if (!practiceArea.trim()) return 'Select a case type to continue.';
    if (selectedTools.length === 0) return 'Select at least one data section to collect.';
    if (selectedTools.includes('forms') && !endpoints.forms_url.trim()) return 'Select a court forms page.';
    if (selectedTools.includes('judges') && !endpoints.judges_url.trim()) return 'Select a judge assignments page.';
    if (selectedTools.includes('fees') && !endpoints.fees_url.trim()) return 'Select a filing fees page.';
    return validateForDiscover();
  };

  const handleDiscover = async () => {
    const validation = validateForDiscover();
    if (validation) {
      setError(validation);
      return;
    }

    try {
      setError(null);
      setDiscovering(true);
      const result = await discoverCourtIntelligenceWizard({
        jurisdiction_code: jurisdictionCode,
        court_name: selectedCourtName,
        root_url: selectedRootUrl,
        practice_area: normalizePracticeArea(practiceArea),
      });
      setDiscoverResult(result);
      setEndpoints((prev) => ({
        forms_url: result.recommended.forms_url || prev.forms_url,
        judges_url: result.recommended.judges_url || prev.judges_url,
        fees_url: result.recommended.fees_url || prev.fees_url,
        rules_url: result.recommended.rules_url || prev.rules_url,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Court page check failed');
    } finally {
      setDiscovering(false);
    }
  };

  const refreshLogs = useCallback(
    async (opId: string) => {
      if (!opId) return;
      try {
        const result = await getCourtIntelligenceWizardLogs(opId);
        setLogs(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load logs');
      }
    },
    [],
  );

  const refreshProgress = useCallback(
    async (opId: string) => {
      if (!opId) return;
      try {
        const next = await getCourtIntelligenceWizardProgress(opId);
        setProgress(next);
        if (FINAL_STATES.has(next.status)) {
          await refreshLogs(opId);
          if (onRunCompleted) await onRunCompleted({ jurisdiction_code: jurisdictionCode, practice_area: practiceArea });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load progress');
      }
    },
    [jurisdictionCode, onRunCompleted, practiceArea, refreshLogs],
  );

  useEffect(() => {
    if (!operationId || !isRunning) return;
    const timer = window.setInterval(() => {
      void refreshProgress(operationId);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [isRunning, operationId, refreshProgress]);

  const handleTrigger = async () => {
    const validation = validateForTrigger();
    if (validation) {
      setError(validation);
      return;
    }

    try {
      setError(null);
      setCopyMessage(null);
      setLogs(null);
      setTriggering(true);
      const result = await triggerCourtIntelligenceWizard({
        jurisdiction_code: jurisdictionCode,
        court_name: selectedCourtName,
        root_url: selectedRootUrl,
        practice_area: normalizePracticeArea(practiceArea),
        tools: selectedTools,
        endpoints: {
          forms_url: endpoints.forms_url.trim(),
          judges_url: endpoints.judges_url.trim(),
          fees_url: endpoints.fees_url.trim(),
          rules_url: endpoints.rules_url.trim() || undefined,
        },
        no_cache: true,
      });
      setOperationId(result.operation_id);
      await refreshProgress(result.operation_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start collection run');
    } finally {
      setTriggering(false);
    }
  };

  const copyExport = async () => {
    if (!logs?.export_text) return;
    try {
      await navigator.clipboard.writeText(logs.export_text);
      setCopyMessage('Run log copied to clipboard.');
    } catch {
      setCopyMessage('Clipboard copy failed. Select and copy the run log manually.');
    }
  };

  return (
    <div className="rounded-lg border border-lex-accent bg-lex-accent-soft dark:border-lex-accent dark:bg-lex-accent-soft">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between p-4 text-left"
      >
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Court Intelligence Wizard</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Choose a court, confirm official court pages, and run a guided collection with progress and failure diagnostics.
          </p>
        </div>
        <svg
          className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!expanded ? null : (<div className="px-4 pb-4">

      {error && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {error}
        </div>
      )}

      {copyMessage && (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
          {copyMessage}
        </div>
      )}

      {!optionsLoading && wizardOptions && (wizardOptions.jurisdictions || []).length === 0 && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <strong>No jurisdictions available.</strong>{' '}
          Configure your states of practice in{' '}
          <span className="font-medium underline">Firm Settings &rarr; Firm Information</span>{' '}
          to see available courts.
        </div>
      )}

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <label className="text-sm text-gray-700 dark:text-gray-200" title="Pick the official jurisdiction code for the court you want to collect from.">
          Jurisdiction
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Select the county/state code for this court.</p>
          <select
            value={jurisdictionCode}
            onChange={(event) => {
              setJurisdictionCode(event.target.value);
              setCourtRegistryId('');
              setDiscoverResult(null);
              setError(null);
            }}
            disabled={optionsLoading}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Select jurisdiction</option>
            {(wizardOptions?.jurisdictions || []).map((item) => (
              <option key={item.jurisdiction_code} value={item.jurisdiction_code}>
                {item.jurisdiction_code} - {item.label}{item.form_count > 0 ? ` (${item.form_count} forms)` : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-700 dark:text-gray-200" title="Pick the exact court. The wizard will use this court's official website.">
          Court
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Select the specific court to crawl.</p>
          <select
            value={courtRegistryId}
            onChange={(event) => {
              setCourtRegistryId(event.target.value);
              setDiscoverResult(null);
              setError(null);
            }}
            disabled={optionsLoading || !jurisdictionCode}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Select court</option>
            {(wizardOptions?.courts || []).map((court) => (
              <option key={court.id} value={court.id}>
                {court.court_name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-700 dark:text-gray-200" title="Choose the case type so forms and deadlines can be classified correctly.">
          Case Type
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This tells the system how to classify extracted data.</p>
          <select
            value={practiceArea}
            onChange={(event) => setPracticeArea(event.target.value)}
            disabled={optionsLoading}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            {(wizardOptions?.practice_area_options || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4 rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-semibold">Court Website:</span>
          <span className="break-all">{selectedRootUrl || 'Not configured yet for selected court'}</span>
          <span className="text-gray-500 dark:text-gray-400">
            Record quality: {wizardOptions?.selected.lifecycle_status || 'draft'}
          </span>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-700 dark:text-gray-200" title="Choose which data sets to collect in this run.">
        Data to Collect
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Turn sections on or off for this run.</p>
        <div className="mt-1 flex flex-wrap gap-3 rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800">
          {([
            { key: 'forms', label: 'Court Forms' },
            { key: 'judges', label: 'Judge Assignments' },
            { key: 'fees', label: 'Filing Fees' },
          ] as Array<{ key: ManualCourtExtractionTool; label: string }>).map((tool) => (
            <label key={tool.key} className="inline-flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={tools[tool.key]}
                onChange={(event) => setTools((prev) => ({ ...prev, [tool.key]: event.target.checked }))}
              />
              {tool.label}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="text-sm text-gray-700 dark:text-gray-200" title="Select the official page where this court publishes forms.">
          Court Forms Page
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Choose the best forms source for this court.</p>
          <select
            value={endpoints.forms_url}
            onChange={(event) => setEndpoints((prev) => ({ ...prev, forms_url: event.target.value }))}
            disabled={optionsLoading || !tools.forms}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Select forms page</option>
            {endpointOptions.forms_url.map((option) => (
              <option key={option.url} value={option.url}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-700 dark:text-gray-200" title="Select the page that lists judge names, departments, or assignments.">
          Judge Assignments Page
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Choose where judges/departments are published.</p>
          <select
            value={endpoints.judges_url}
            onChange={(event) => setEndpoints((prev) => ({ ...prev, judges_url: event.target.value }))}
            disabled={optionsLoading || !tools.judges}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Select judge page</option>
            {endpointOptions.judges_url.map((option) => (
              <option key={option.url} value={option.url}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-700 dark:text-gray-200" title="Select the page or PDF that contains filing fee amounts.">
          Filing Fees Page
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Choose the source for filing and motion fees.</p>
          <select
            value={endpoints.fees_url}
            onChange={(event) => setEndpoints((prev) => ({ ...prev, fees_url: event.target.value }))}
            disabled={optionsLoading || !tools.fees}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Select fees page</option>
            {endpointOptions.fees_url.map((option) => (
              <option key={option.url} value={option.url}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-700 dark:text-gray-200" title="Optional: select the page with local rules and standing orders.">
          Local Rules Page (Optional)
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Use this when you want rules captured in this run.</p>
          <select
            value={endpoints.rules_url}
            onChange={(event) => setEndpoints((prev) => ({ ...prev, rules_url: event.target.value }))}
            disabled={optionsLoading}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Skip local rules for now</option>
            {endpointOptions.rules_url.map((option) => (
              <option key={option.url} value={option.url}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={handleDiscover}
          disabled={discovering || optionsLoading}
          className="rounded-md border border-lex-accent bg-white px-3 py-2 text-sm font-semibold text-lex-accent hover:bg-lex-state-hover disabled:opacity-60 dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent"
        >
          {discovering ? 'Checking...' : '1. Check Court Pages'}
        </button>
        <button
          onClick={handleTrigger}
          disabled={triggering || optionsLoading || selectedTools.length === 0}
          className="rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 dark:border-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-100"
        >
          {triggering ? 'Starting...' : '2. Run Collection'}
        </button>
        {operationId && (
          <button
            onClick={() => void refreshProgress(operationId)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            Refresh Progress
          </button>
        )}
        {operationId && (
          <button
            onClick={() => void refreshLogs(operationId)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            Refresh Logs
          </button>
        )}
      </div>

      {discoverResult && (
        <div className="mt-4 rounded-md border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-wrap items-center gap-3">
            <span className={discoverResult.readiness.can_run_full_extraction ? 'text-emerald-600' : 'text-amber-600'}>
              {discoverResult.readiness.can_run_full_extraction ? 'Ready to run collection' : 'Needs page selections'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Profile match: {discoverResult.profile_match.found ? 'Yes' : 'No'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Missing: {discoverResult.readiness.missing_required.join(', ') || 'none'}
            </span>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {(['forms', 'judges', 'fees', 'rules'] as const).map((type) => (
              <div key={type} className="rounded-md border border-gray-200 p-2 text-xs dark:border-gray-700">
                <p className="font-semibold uppercase text-gray-700 dark:text-gray-200">{type}</p>
                {(discoverResult.probes[type] || []).slice(0, 3).map((probe) => (
                  <div key={`${type}-${probe.url}`} className="mt-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-gray-700 dark:text-gray-300">{probe.url}</span>
                      <span className={probe.http_ok ? 'text-emerald-600' : 'text-rose-600'}>
                        {probe.score}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {probe.status_code ?? 'n/a'} · {probe.source} · {probe.reasons.join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {progress && (
        <div className="mt-4 rounded-md border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`font-semibold ${statusClass(progress.status)}`}>Run status: {progress.status}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Operation: {progress.operation_id}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {progress.completion_percent.toFixed(0)}% complete
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full bg-lex-accent"
              style={{ width: `${Math.max(0, Math.min(100, progress.completion_percent))}%` }}
            />
          </div>
          <div className="mt-3 space-y-2">
            {progress.steps.map((step) => (
              <div key={step.tool_id} className="rounded border border-gray-200 p-2 text-xs dark:border-gray-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{step.tool_id}</span>
                  <span className={statusClass(step.status)}>{step.status}</span>
                </div>
                <div className="mt-1 text-gray-500 dark:text-gray-400">
                  records: {step.records_extracted ?? 0} · warnings: {step.warning_count}
                </div>
                {step.error && <div className="mt-1 text-rose-600 dark:text-rose-300">{step.error}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {logs && (
        <div className="mt-4 rounded-md border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`font-semibold ${statusClass(logs.status)}`}>Log status: {logs.status}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              started: {formatDate(logs.started_at)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              completed: {formatDate(logs.completed_at)}
            </span>
          </div>
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            Success: {logs.summary.success_count} · Failures: {logs.summary.failure_count} · Warnings:{' '}
            {logs.summary.warning_count} · Records: {logs.summary.extracted_record_total}
          </div>

          {logs.failures.length > 0 && (
            <div className="mt-3 space-y-2">
              {logs.failures.map((failure) => (
                <div key={`${failure.tool_id}-${failure.requested_endpoint || 'none'}`} className="rounded border border-rose-200 bg-rose-50 p-2 text-xs dark:border-rose-800 dark:bg-rose-900/20">
                  <p className="font-semibold text-rose-700 dark:text-rose-200">{failure.tool_id}</p>
                  <p className="mt-1 text-rose-700 dark:text-rose-200">{failure.error || 'No explicit error returned.'}</p>
                  <p className="mt-1 text-gray-700 dark:text-gray-200">
                    Likely causes: {failure.likely_causes.join(' | ')}
                  </p>
                  <p className="mt-1 text-gray-700 dark:text-gray-200">
                    Suggested fixes: {failure.suggested_fixes.join(' | ')}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={copyExport}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              Copy Run Log
            </button>
          </div>
          <textarea
            value={logs.export_text}
            readOnly
            className="mt-2 h-36 w-full rounded border border-gray-300 bg-gray-50 px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-900"
          />
        </div>
      )}

      </div>)}
    </div>
  );
};

export default CourtIntelligenceWizardPanel;
